import { Col, Container, Row, Table } from "react-bootstrap";
import { RouteProps } from "react-router";
import Content from "Types/Content";

const GitFlow: React.FC<RouteProps> = () => {
  return (
    <Container className='pt-3'>
      <h1>
        <a href='https://www.atlassian.com/es/git/tutorials/comparing-workflows/gitflow-workflow'>
          Git Flow
        </a>
      </h1>
      <p className='text-justify'>
        El flujo de trabajo de GitFlow es un diseño de flujo de trabajo de{" "}
        <a href='/content/Git'>Git</a> que fue publicado por primera vez y
        popularizado por Vincent Driessen en nvie. El flujo de trabajo de
        Gitflow define un modelo estricto de ramificación diseñado alrededor de
        la publicación del proyecto. Proporciona un marco sólido para gestionar
        proyectos más grandes. Antes de entender GitFlow deberías tener en claro
        como usar <a href='/content/Git'>Git</a>, si todavía no lo tenés tan
        claro podes entrar <a href='/content/Git'>acá</a> para leer más y
        aclarar los conceptos.
      </p>
      <p className='text-justify'>
        Gitflow es una especie de idea abstracta de un flujo de trabajo de Git.
        Esto quiere decir que ordena qué tipo de ramas se deben configurar y
        cómo fusionarlas. Si bien existen herraminetas como 'git-flow' que te
        permiten manejar el esquema de git flow con comandos, en el equipo de
        desarrollo usamos git sin ninguna abstracción intermedia, y seguimos el
        esquema de git-flow a mano. Creemos que esto nos da un poco más de
        flexibilidad para trabajar y nos elimina una dependencia (que nunca
        viene mal tener menos dependencias).
      </p>
      <p>
        Siendo Gitflow una 'idea', cada empresa y equipo de desarrollo puede
        tener su propia implementación del estanadar. En el caso de crombie, lo
        importante que tenes que saber es que existen 6 ramas que todo
        repositorio de Crombie que siga el esquema GitFlow puede tener:
      </p>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Rama</th>
            <th>Descripción</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>master</td>
            <td>
              <strong>
                La rama master será encargada de almacenar todas las versiones
                entregadas del sistema.{" "}
              </strong>
              La razón de existir de esta rama, es poder 'aislar' las versiones
              'finales' del sistema que no querramos ensuciar con el desarrollo.
              Son ramas 'lentas' en las que ningún desarrollador del equipo
              puede hacer commit, y{" "}
              <strong>
                solo se puede incorporar algo a la rama master a partir de un
                pull request aprobado por tercero cuando haya sido testeado y
                esté listo para ser visto por el cliente.
              </strong>{" "}
              (o ya ha sido entregado al cliente)
            </td>
          </tr>
          <tr>
            <td>develop</td>
            <td>
              <strong>
                La rama develop será encargada de almacenar todas las versiones
                de desarrollo.{" "}
              </strong>
              Si bien la rama develop tiene varias de las restricciones que
              tiene master, al no ser un entregable visible por el cliente se
              puede tener más tranquilidad respecto del nivel de revisión y
              testeo que debe tener cada versión de esta rama. Dicho eso, en la
              rama develop, al igual que en la rama master,
              <strong>
                solo se puede incorporar código a partir de un Pull Request
                aprobado por tercero.
              </strong>
            </td>
          </tr>
          <tr>
            <td>release/*</td>
            <td>
              <strong>
                La rama release será la encargada de preparar una versión para
                ser enviada a master (entregada al cliente).{" "}
              </strong>
              Esta rama, a diferencia de master y develop, no es única, se crea
              para hacer cada release, y muere una vez que es integrada a master
              y develop a través de un Pull Request.
            </td>
          </tr>
          <tr>
            <td>bugfix/*</td>
            <td>
              <strong>
                La rama bugfix/* será la encargada de corregir un bug del
                sistema.{" "}
              </strong>
              Al igual que release, esta rama no es única, sino que se crea a
              partir de un issue reportado previamente en en Jira, y muere una
              vez que se realiza un pull request de la rama a develop y dicho
              pull request es aprobado e integrado.
            </td>
          </tr>
          <tr>
            <td>feature/*</td>
            <td>
              <strong>
                La rama feature/* será la encargada de agregar nueva
                funcionalidad al sistema.{" "}
              </strong>
              Existirá una rama feature por cada funcionalidad nueva que se
              agregue al sistema, y se creará la rama desde un issue reportado
              previamente en Jira. La rama feature muere una vez que se realiza
              un pull request a develop y dicho pull request es aprobado e
              integrado.
            </td>
          </tr>
          <tr>
            <td>hotfix/*</td>
            <td>
              <strong>
                La rama hotfix/* está destinada a cualquier arreglo en 'vivo'
                que se tenga que hacer del sistema.{" "}
              </strong>
              Si bien existe como una posibilidad, solo debe usarse en caso de
              necesidad. Si el issue no es critico, es mejor anotarlo para
              resolver de manera normal a partir de una rama bugfix, y así
              entregar su solución testeada y aprobada en un entregable
              posterior.
            </td>
          </tr>
        </tbody>
      </Table>
      <Row className='justify-content-center'>
        <Col xs={12} md={10} lg={8}>
          <img
            src='https://wac-cdn.atlassian.com/dam/jcr:61ccc620-5249-4338-be66-94d563f2843c/05%20(2).svg?cdnVersion=1510'
            loading='lazy'
            className='mt-3 mb-5'
            alt='Flujo de trabajo de Git flow: ramas históricas'
          />
        </Col>
      </Row>
    </Container>
  );
};

export default new Content({
  Component: GitFlow,
  title: "Git Flow",
  route: "git-flow",
  tags: [
    "git",
    "gitflow",
    "version",
    "version control",
    "bitbucket",
    "repository"
  ]
});
